import { AlertTriangle, Check, Loader2, RotateCw, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useCaseNotifications,
  useForceAccept,
  useResendNotification,
} from '@/lib/notifications/use-notifications';
import { NOTIFICATION_CHANNELS, type NotificationChannel } from '@/lib/notifications/types';
import { NotificationStatusBadges } from './notification-status-badges';
import { NotificationSendHistory } from './notification-send-history';
import { ForceAcceptDialog } from './force-accept-dialog';

// The per-PNR-group notification/acceptance surface, embedded in the roster row
// (pnr-roster-row · PnrRow). One shared `useCaseNotifications(caseUrn)` query
// backs every row (React Query dedups → one fetch, one WS subscription). Renders
// the four UX states (loading / error / empty / success); on success shows the
// status badges + the resend (channel-select) menu + the supervisor force action.

/** Translation KEY per notification channel — this module holds keys, never
 *  language (the copy lives under `channel.*` in the `pnr` bundles). */
const CHANNEL_KEY: Record<NotificationChannel, string> = {
  SMS: 'channel.sms',
  WHATSAPP: 'channel.whatsapp',
  EMAIL: 'channel.email',
  VOICE: 'channel.voice',
  PUSH: 'channel.push',
};

interface PnrNotificationCellProps {
  caseUrn: string;
  subCaseUrn: string;
  /** Friendly label for the group, used in the force-accept confirm copy. */
  groupLabel: string;
}

function errMessage(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
    return (e as { message: string }).message;
  }
  return fallback;
}

export function PnrNotificationCell({ caseUrn, subCaseUrn, groupLabel }: PnrNotificationCellProps) {
  const { t } = useTranslation('pnr');
  const { rowFor, isLoading, isError, refetch } = useCaseNotifications(caseUrn);
  const resend = useResendNotification(caseUrn);
  const force = useForceAccept(caseUrn);
  const row = rowFor(subCaseUrn);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [resendStatus, setResendStatus] = useState<{ tone: 'success' | 'warning' | 'error'; message: string } | null>(null);

  // Loading — the shared query hasn't resolved yet.
  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="notification-loading">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        {t('notification.loading')}
      </div>
    );
  }

  // Error — BFF unreachable; inline message + retry (no auto-poll).
  if (isError) {
    return (
      <div className="flex items-center gap-2 text-xs text-destructive" data-testid="notification-error">
        <span>{t('notification.statusUnavailable')}</span>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          data-testid="notification-retry"
        >
          <RotateCw className="h-3 w-3" aria-hidden />
          {t('notification.retry')}
        </button>
      </div>
    );
  }

  // Sending the passenger their CURRENT state is always allowed — before any
  // offer exists, on a care-only group with nothing but a hold, and to a
  // passenger who has never been sent anything. The acceptance gate only decides
  // whether the supervisor FORCE action applies, never whether we can write.
  const canForce = row?.acceptanceState === 'awaiting';

  const doResend = (channel?: NotificationChannel): void => {
    setResendStatus(null);
    resend.mutate(
      { subCaseUrn, ...(channel ? { channel } : {}) },
      {
        onSuccess: (res) => {
          if (res.status === 'queued') {
            setResendStatus({
              tone: 'success',
              message: channel
                ? t('notification.sentVia', { channel: t(CHANNEL_KEY[channel]) })
                : t('notification.sent'),
            });
          } else {
            setResendStatus({ tone: 'warning', message: t('notification.redispatchUnavailable') });
          }
        },
        onError: (e) => setResendStatus({ tone: 'error', message: errMessage(e, t('notification.requestFailed')) }),
      },
    );
  };

  // Force-accept: success is reflected by the acceptance state change in the UI;
  // errors are shown inline in the ForceAcceptDialog via force.error.
  const doForce = (reason: string): void => {
    force.mutate({ subCaseUrn, body: { offerHash: row?.offerHash ?? '', ...(reason ? { reason } : {}) } });
  };

  const sends = row?.sends ?? [];
  const hasHistory = sends.length > 1;

  return (
    <div className="flex flex-col items-start gap-1.5" data-testid="notification-success">
      {row ? (
        <NotificationStatusBadges
          deliveryState={row.deliveryState}
          acceptanceState={row.acceptanceState}
          channel={row.channel}
        />
      ) : (
        <span className="text-xs text-muted-foreground" data-testid="notification-empty">
          {t('notification.current.unknown')}
        </span>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={resend.isPending}
              data-testid="resend-trigger"
            >
              {resend.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Send className="h-3.5 w-3.5" aria-hidden />
              )}
              {t('notification.resend')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onSelect={() => doResend()} data-testid="resend-current">
              {row?.channel
                ? t('notification.resendWithChannel', { channel: t(CHANNEL_KEY[row.channel]) })
                : t('notification.resend')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t('notification.sendVia')}</DropdownMenuLabel>
            {NOTIFICATION_CHANNELS.map((ch) => (
              <DropdownMenuItem
                key={ch}
                onSelect={() => doResend(ch)}
                data-testid={`resend-channel-${ch}`}
              >
                {t(CHANNEL_KEY[ch])}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {hasHistory ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setHistoryExpanded(!historyExpanded)}
            data-testid="notification-history-toggle"
          >
            {historyExpanded ? (
              <ChevronUp className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            )}
            {t('notification.history', { count: sends.length })}
          </Button>
        ) : null}

        {canForce ? (
          <ForceAcceptDialog
            key={row?.offerHash ?? 'missing-offer'}
            groupLabel={groupLabel}
            pending={force.isPending}
            offerHashMissing={!row?.offerHash}
            error={force.isError ? errMessage(force.error, t('notification.requestFailed')) : null}
            onConfirm={doForce}
          />
        ) : null}
      </div>
      {hasHistory && historyExpanded ? (
        <NotificationSendHistory sends={sends} />
      ) : null}
      {resendStatus ? (
        <div
          className={`mt-1.5 flex items-center gap-1.5 text-xs ${
            resendStatus.tone === 'success'
              ? 'text-emerald-700 dark:text-emerald-400'
              : resendStatus.tone === 'warning'
                ? 'text-amber-700 dark:text-amber-400'
                : 'text-destructive'
          }`}
          role={resendStatus.tone === 'error' ? 'alert' : 'status'}
          data-testid="notification-resend-status"
        >
          {resendStatus.tone === 'success' ? (
            <Check className="h-3 w-3" aria-hidden />
          ) : (
            <AlertTriangle className="h-3 w-3" aria-hidden />
          )}
          {resendStatus.message}
        </div>
      ) : null}
    </div>
  );
}
